/**
 * Shared helpers for the workspace invite flow — the single place that
 * knows how a free-text "email or username" recipient is classified and
 * how a pending invitation is labeled. Used by both invite surfaces
 * (WorkspaceControlsPage, fias360 TeamSection) and UserSearchCombobox
 * so their detection rules cannot drift apart.
 */

import type { VaultInvitationDto, VaultInviteInput } from './api';

/**
 * True when the value reads as an email address: any "@" past a leading
 * handle sigil (a leading "@" alone marks a username, e.g. "@alice").
 */
export function isEmailLikeRecipient(value: string): boolean {
  return value.trim().replace(/^@/, '').includes('@');
}

export type InviteRecipientParse =
  | {
      ok: true;
      payload: { inviteeEmail: string } | { inviteeUsername: string };
      /** Human-readable form for toasts: the address, or `@username`. */
      label: string;
    }
  | { ok: false; error: string };

/**
 * Classify a typed recipient into the invite payload the API expects,
 * or a user-facing validation error. Mirrors the server's rules
 * (usernames are ≥2 chars) so obvious mistakes fail with a friendly
 * message instead of a generic 400.
 */
export function parseInviteRecipient(recipient: string): InviteRecipientParse {
  const stripped = recipient.trim().replace(/^@/, '');
  if (!stripped) {
    return { ok: false, error: 'Enter an email address or username' };
  }
  if (stripped.includes('@')) {
    return { ok: true, payload: { inviteeEmail: stripped }, label: stripped };
  }
  if (stripped.length < 2) {
    return { ok: false, error: 'Usernames are at least 2 characters' };
  }
  return { ok: true, payload: { inviteeUsername: stripped }, label: `@${stripped}` };
}

/**
 * Owner-side label for a pending invitation: @username for user-keyed
 * invites, the address for email invites.
 */
export function invitationRecipientLabel(
  i: Pick<VaultInvitationDto, 'inviteeEmail' | 'inviteeUsername'>,
): string {
  if (i.inviteeUsername) return `@${i.inviteeUsername}`;
  return i.inviteeEmail ?? 'Unknown invitee';
}

/**
 * True when an owner-side invitation has lapsed. Prefers the server's
 * derived `status`; falls back to comparing `expiresAt` for rows that
 * predate the field. An expired invitation can no longer be accepted,
 * but it still appears in the Holder's list until removed or resent.
 */
export function isInvitationExpired(
  i: Pick<VaultInvitationDto, 'status' | 'expiresAt'>,
  now: number = Date.now(),
): boolean {
  if (i.status) return i.status === 'expired';
  return new Date(i.expiresAt).getTime() <= now;
}

/**
 * The invite payload that re-sends an invitation to the same recipient
 * with the same role, or null when the recipient can't be addressed
 * (a user-keyed invite whose username didn't resolve). Re-inviting
 * over an expired invitation replaces it server-side.
 */
export function resendInvitePayload(
  i: Pick<VaultInvitationDto, 'inviteeEmail' | 'inviteeUsername' | 'role'>,
): VaultInviteInput | null {
  if (i.inviteeUsername) return { inviteeUsername: i.inviteeUsername, role: i.role };
  if (i.inviteeEmail) return { inviteeEmail: i.inviteeEmail, role: i.role };
  return null;
}
